import React, { useState, useContext, useEffect, useMemo, useRef } from "react";
import { useAccountsNav } from "../../utils/moduleNav";
import {
  Box,
  Grid,
  TextField,
  Button,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Divider,
  Card,
  CardContent,
  InputAdornment,
  Chip,
  Autocomplete,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
  ToggleButtonGroup,
  ToggleButton,
} from "@mui/material";
import {
  Search,
  Download,
  Refresh,
  AccountBalance,
  Close,
  CalendarToday,
  DateRange,
} from "@mui/icons-material";
import { format } from "date-fns";
import { AutoTaskAuthContext } from "../../Context/AutoTaskContext";
import axios from "axios";
import jsPDF from "jspdf";
import "jspdf-autotable";
import TrialBalanceAllGroups from "./TrialBalanceAllGroups";

import { useLedgerState } from "../../Context/LedgerStateContext";
import { useLocation } from "react-router-dom";

const STATIC_CATEGORY_OPTIONS = [
  {
    account_id: "ALL",
    account_name: "All",
    account_category: null,
    isStatic: true,
  },
  {
    account_id: "INCOME",
    account_name: "Income",
    account_category: "INCOME",
    isStatic: true,
  },
  {
    account_id: "EXPENSE",
    account_name: "Expense",
    account_category: "EXPENSE",
    isStatic: true,
  },
  {
    account_id: "LIABILITY",
    account_name: "Liability",
    account_category: "LIABILITY",
    isStatic: true,
  },
  {
    account_id: "ASSET",
    account_name: "Asset",
    account_category: "ASSET",
    isStatic: true,
  },
];

const TrialBalanceByGroup = ({ tabIndex }) => {
  const { userInfo, defaultFY, defaultFYId } =
    useContext(AutoTaskAuthContext) || {};
  const store_id = userInfo?.store_id || 0;
  // Keeps the voucher drill-down inside the host module (pawn keeps its chrome).
  const { go: goAccounts } = useAccountsNav();
  const location = useLocation();
  const { ledgerState, updateTabState } = useLedgerState();

  const isReturningFromEntry = location.state?.fromAccountsEntry === true;
  const highlightVoucher = location.state?.highlightVoucher || null;
  const shouldReopenDialog = location.state?.reopenDialog || false;
  const dialogAccountId = location.state?.dialogAccountId || null;

  const savedState = isReturningFromEntry
    ? ledgerState.trialBalanceByGroup
    : null;

  // ── Filter state ──
  const [selectedGroup, setSelectedGroup] = useState(
    savedState?.selectedGroup ?? STATIC_CATEGORY_OPTIONS[0],
  );
  const [filterMode, setFilterMode] = useState(
    savedState?.filterMode ?? "as_on",
  );
  const [asOnDate, setAsOnDate] = useState(
    savedState?.asOnDate ?? format(new Date(), "yyyy-MM-dd"),
  );
  const [fromDate, setFromDate] = useState(
    savedState?.fromDate ??
      format(
        new Date(new Date().getFullYear(), new Date().getMonth(), 1),
        "yyyy-MM-dd",
      ),
  );
  const [toDate, setToDate] = useState(
    savedState?.toDate ?? format(new Date(), "yyyy-MM-dd"),
  );

  const [searchText, setSearchText] = useState("");
  const [dynamicGroups, setDynamicGroups] = useState([]);
  const [loadingGroups, setLoadingGroups] = useState(false);
  const [groupsError, setGroupsError] = useState("");
  const [loadingReport, setLoadingReport] = useState(false);
  const [reportData, setReportData] = useState(savedState?.reportData ?? null);
  const [allGroupsData, setAllGroupsData] = useState(
    savedState?.allGroupsData ?? null,
  );
  const [error, setError] = useState("");

  // ── Dialog state ──
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState(
    savedState?.selectedAccount ?? null,
  );
  const [ledgerData, setLedgerData] = useState(savedState?.ledgerData ?? null);
  const [loadingLedger, setLoadingLedger] = useState(false);
  const [ledgerError, setLedgerError] = useState("");
  const [dialogMonthFilter, setDialogMonthFilter] = useState(
    savedState?.dialogMonthFilter ?? "",
  );

  // ── Ref to scroll highlighted txn row inside dialog ──
  const highlightTxnRowRef = useRef(null);
  // ── Ref to prevent double-fire of reopen logic ──
  const reopenHandledRef = useRef(false);

  // ── Restore dialog when returning from accounts-entry ──
  // Wait until reportData/allGroupsData is populated AND we have a dialogAccountId
  useEffect(() => {
    if (!shouldReopenDialog || !dialogAccountId || reopenHandledRef.current)
      return;

    // Determine if we have something to search through
    const accountsList =
      reportData?.trial_balance || allGroupsData?.categories || [];

    // For allGroupsData we need to find in nested tree
    const findInTree = (nodes) => {
      for (const node of nodes) {
        if (String(node.account_id) === String(dialogAccountId)) return node;
        if (node.children?.length) {
          const found = findInTree(node.children);
          if (found) return found;
        }
      }
      return null;
    };

    let account = null;
    if (reportData?.trial_balance) {
      account = reportData.trial_balance.find(
        (a) => String(a.account_id) === String(dialogAccountId),
      );
    } else if (allGroupsData?.categories) {
      account = findInTree(allGroupsData.categories);
    }

    if (account) {
      reopenHandledRef.current = true;
      if (savedState?.ledgerData) {
        // Restore saved ledger immediately
        setSelectedAccount(account);
        setLedgerData(savedState.ledgerData);
        setDialogMonthFilter(savedState.dialogMonthFilter ?? "");
        setDialogOpen(true);
      } else {
        openAccountLedgerDialog(account);
      }
    }
  }, [shouldReopenDialog, dialogAccountId, reportData, allGroupsData]);

  // ── Scroll to highlighted row inside dialog ──
  useEffect(() => {
    if (
      dialogOpen &&
      highlightVoucher &&
      ledgerData &&
      highlightTxnRowRef.current
    ) {
      setTimeout(() => {
        highlightTxnRowRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }, 200);
    }
  }, [dialogOpen, highlightVoucher, ledgerData]);

  // ── Persist to context ──
  useEffect(() => {
    updateTabState(tabIndex, {
      selectedGroup,
      filterMode,
      asOnDate,
      fromDate,
      toDate,
      reportData,
      allGroupsData,
      selectedAccount,
      ledgerData,
      dialogMonthFilter,
    });
  }, [
    selectedGroup,
    filterMode,
    asOnDate,
    fromDate,
    toDate,
    reportData,
    allGroupsData,
    selectedAccount,
    ledgerData,
    dialogMonthFilter,
    tabIndex,
    updateTabState,
  ]);

  useEffect(() => {
    if (!store_id) return;
    fetchGroups();
  }, [store_id]);

  const fetchGroups = async () => {
    try {
      setLoadingGroups(true);
      setGroupsError("");
      const { data } = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/api/accounts/accounts/${store_id}/groups-only`,
      );
      if (data.statusCode === 200 && Array.isArray(data.data)) {
        setDynamicGroups(data.data);
      } else {
        setGroupsError("Failed to load groups");
      }
    } catch (err) {
      setGroupsError("Error loading groups");
    } finally {
      setLoadingGroups(false);
    }
  };

  const allOptions = [
    ...STATIC_CATEGORY_OPTIONS,
    ...dynamicGroups.map((g) => ({
      account_id: g.account_id,
      account_name: g.account_name,
      account_category: g.account_category,
      parent_account_id: g.parent_account_id,
      isStatic: false,
    })),
  ];

  const getGroupIdPayload = (option) => option?.account_id ?? null;

  const handleGenerateReport = async () => {
    setError("");

    if (filterMode === "as_on" && !asOnDate) {
      setError("Please select a date");
      return;
    }
    if (filterMode === "between" && (!fromDate || !toDate)) {
      setError("Please select both from and to dates");
      return;
    }
    if (filterMode === "between" && new Date(fromDate) > new Date(toDate)) {
      setError("From date must be before or equal to To date");
      return;
    }
    if (!selectedGroup) {
      setError("Please select a group");
      return;
    }

    const groupIdPayload = getGroupIdPayload(selectedGroup);

    const buildPayload = () => ({
      store_id,
      filter_mode: filterMode,
      ...(filterMode === "as_on"
        ? { as_on_date: asOnDate }
        : { from_date: fromDate, to_date: toDate }),
    });

    if (groupIdPayload === "ALL") {
      try {
        setLoadingReport(true);
        setReportData(null);
        setAllGroupsData(null);

        const { data: json } = await axios.post(
          `${process.env.REACT_APP_API_BASE_URL}/api/accounts/trial-balance/all-groups/${defaultFYId}`,
          buildPayload(),
        );

        if (!json.success) {
          setError(json.message || "Error generating trial balance");
          return;
        }
        setAllGroupsData(json.data);
        setSearchText("");
      } catch (err) {
        setError(
          err?.response?.data?.message || "Error generating trial balance",
        );
      } finally {
        setLoadingReport(false);
      }
      return;
    }

    try {
      setLoadingReport(true);
      setReportData(null);

      const { data: json } = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/accounts/trial-balance/by-group/${defaultFYId}`,
        { ...buildPayload(), group_id: groupIdPayload },
      );

      if (!json.success) {
        setError(json.message || "Error generating trial balance");
        return;
      }
      setReportData(json.data);
      setAllGroupsData(null);
      setSearchText("");
    } catch (err) {
      setError(
        err?.response?.data?.message || "Error generating trial balance",
      );
    } finally {
      setLoadingReport(false);
    }
  };

  const handleReset = () => {
    setReportData(null);
    setAllGroupsData(null);
    setError("");
    setSearchText("");
    setSelectedGroup(null);
    setFilterMode("as_on");
  };

  // ── Open account ledger dialog (fetches fresh data) ──
  const openAccountLedgerDialog = async (acc) => {
    setSelectedAccount(acc);
    setDialogOpen(true);
    setLedgerError("");
    setLedgerData(null);
    setDialogMonthFilter("");

    try {
      setLoadingLedger(true);
      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/accounts/individual-ledger`,
        {
          store_id: parseInt(store_id),
          filter_type: "Account",
          account_id: acc.account_id,
          fy_id: defaultFYId,
          from_date: filterMode === "as_on" ? `${defaultFY}-04-01` : fromDate,
          to_date: filterMode === "as_on" ? asOnDate : toDate,
        },
      );
      if (response.data.success) {
        setLedgerData(response.data.data);
      } else {
        setLedgerError(response.data.message || "Failed to fetch ledger");
      }
    } catch (err) {
      setLedgerError(err.response?.data?.message || "Error fetching ledger");
    } finally {
      setLoadingLedger(false);
    }
  };

  // ── Double-click row in trial balance table ──
  const handleAccountRowDoubleClick = (acc) => {
    reopenHandledRef.current = false;
    openAccountLedgerDialog(acc);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    // Keep selectedAccount & ledgerData so they survive the context persist
  };

  // ── Double-click transaction row inside dialog → navigate ──
  const handleTxnRowDoubleClick = (txn) => {
    goAccounts("/accounts-entry", {
      state: {
        voucher_no: txn.voucher_number,
        fromLedger: true,
        // Return path metadata
        highlightVoucher: txn.voucher_number,
        reopenDialog: true,
        dialogAccountId: selectedAccount?.account_id,
      },
    });
  };

  const filteredTransactions = useMemo(() => {
    if (!ledgerData?.transactions) return [];
    const sorted = [...ledgerData.transactions].sort(
      (a, b) => new Date(b.date) - new Date(a.date),
    );
    if (!dialogMonthFilter) return sorted;
    return sorted.filter(
      (txn) => format(new Date(txn.date), "yyyy-MM") === dialogMonthFilter,
    );
  }, [ledgerData, dialogMonthFilter]);

  const formatCurrency = (amount) =>
    new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 2,
    }).format(amount || 0);

  const formatAmount = (amount) =>
    new Intl.NumberFormat("en-IN", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount || 0);

  const filteredAccounts =
    reportData?.trial_balance?.filter((acc) => {
      if (!searchText.trim()) return true;
      const q = searchText.toLowerCase();
      return (
        acc.account_name?.toLowerCase().includes(q) ||
        acc.account_id?.toString().includes(q) ||
        acc.account_category?.toLowerCase().includes(q)
      );
    }) || [];

  const generatePDF = () => {
    if (!reportData) return;
    const doc = new jsPDF({
      orientation: filterMode === "between" ? "landscape" : "portrait",
      unit: "mm",
      format: "a4",
    });
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 10;
    const storeName = userInfo?.store_name || "";

    doc.setFontSize(11);
    doc.setFont("helvetica", "bold");
    doc.text(storeName, pageWidth / 2, 12, { align: "center" });
    doc.setFontSize(9);
    doc.text("Trial Balance by Group", pageWidth / 2, 18, { align: "center" });
    doc.setFontSize(8);
    doc.setFont("helvetica", "normal");

    if (filterMode === "as_on") {
      doc.text(
        `As On: ${format(new Date(asOnDate), "dd-MM-yyyy")}`,
        margin,
        24,
      );
    } else {
      doc.text(
        `Period: ${format(new Date(fromDate), "dd-MM-yyyy")} to ${format(
          new Date(toDate),
          "dd-MM-yyyy",
        )}`,
        margin,
        24,
      );
    }

    doc.text(`Group: ${reportData.group_name}`, margin, 29);

    let tableHead, tableData, columnStyles;

    if (filterMode === "as_on") {
      tableHead = [["#", "Acc. ID", "Account Name", "Debit", "Credit"]];
      tableData = filteredAccounts.map((acc, idx) => {
        const isDebit = acc.balance_type === "DEBIT";
        return [
          String(idx + 1),
          String(acc.account_id),
          acc.account_name,
          isDebit ? formatAmount(acc.closing_balance) : "",
          !isDebit ? formatAmount(acc.closing_balance) : "",
        ];
      });
      const totalDebit = filteredAccounts
        .filter((a) => a.balance_type === "DEBIT")
        .reduce((s, a) => s + a.closing_balance, 0);
      const totalCredit = filteredAccounts
        .filter((a) => a.balance_type === "CREDIT")
        .reduce((s, a) => s + a.closing_balance, 0);
      tableData.push([
        "",
        "",
        "Grand Total",
        totalDebit > 0 ? formatAmount(totalDebit) : "",
        totalCredit > 0 ? formatAmount(totalCredit) : "",
      ]);
      columnStyles = {
        0: { halign: "center", cellWidth: 8 },
        1: { halign: "center", cellWidth: 16 },
        2: { halign: "left", cellWidth: 70 },
        3: { halign: "right", cellWidth: 30 },
        4: { halign: "right", cellWidth: 30 },
      };
    } else {
      tableHead = [
        [
          "#",
          "Acc. ID",
          "Account Name",
          "Opening",
          "Period Db",
          "Period Cr",
          "Closing",
        ],
      ];
      tableData = filteredAccounts.map((acc, idx) => {
        const isOpeningDebit = acc.period_opening_balance_type === "DEBIT";
        const isClosingDebit = acc.balance_type === "DEBIT";
        return [
          String(idx + 1),
          String(acc.account_id),
          acc.account_name,
          acc.period_opening_balance > 0
            ? `${formatAmount(acc.period_opening_balance)} ${
                isOpeningDebit ? "Db" : "Cr"
              }`
            : "-",
          acc.period_debit > 0 ? formatAmount(acc.period_debit) : "-",
          acc.period_credit > 0 ? formatAmount(acc.period_credit) : "-",
          acc.closing_balance > 0
            ? `${formatAmount(acc.closing_balance)} ${
                isClosingDebit ? "Db" : "Cr"
              }`
            : "-",
        ];
      });
      tableData.push(["", "", "Grand Total", "", "", "", ""]);
      columnStyles = {
        0: { halign: "center", cellWidth: 8 },
        1: { halign: "center", cellWidth: 12 },
        2: { halign: "left", cellWidth: 60 },
        3: { halign: "right", cellWidth: 30 },
        4: { halign: "right", cellWidth: 25 },
        5: { halign: "right", cellWidth: 25 },
        6: { halign: "right", cellWidth: 30 },
      };
    }

    doc.autoTable({
      startY: 34,
      head: tableHead,
      body: tableData,
      theme: "grid",
      styles: {
        fontSize: 7,
        cellPadding: 1.5,
        lineColor: [0, 0, 0],
        lineWidth: 0.1,
        textColor: [0, 0, 0],
      },
      headStyles: {
        fillColor: [255, 255, 255],
        textColor: [0, 0, 0],
        fontStyle: "bold",
        halign: "center",
        lineWidth: 0.3,
        fontSize: 8,
      },
      columnStyles,
      bodyStyles: { textColor: [0, 0, 0] },
      didParseCell(data) {
        if (
          data.row.index === tableData.length - 1 &&
          data.section === "body"
        ) {
          data.cell.styles.fontStyle = "bold";
          data.cell.styles.fillColor = [245, 245, 245];
        }
      },
      margin: { left: margin, right: margin },
    });

    const filename =
      filterMode === "as_on"
        ? `TrialBalance_${reportData.group_name}_AsOn_${format(
            new Date(asOnDate),
            "dd-MM-yyyy",
          )}.pdf`
        : `TrialBalance_${reportData.group_name}_${format(
            new Date(fromDate),
            "dd-MM-yyyy",
          )}_to_${format(new Date(toDate), "dd-MM-yyyy")}.pdf`;

    doc.save(filename);
  };

  return (
    <Box>
      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
          Trial Balance by Group
        </Typography>

        <Box sx={{ mb: 2, display: "flex", alignItems: "center", gap: 2 }}>
          <ToggleButtonGroup
            value={filterMode}
            exclusive
            onChange={(e, newMode) => {
              if (newMode !== null) {
                setFilterMode(newMode);
                setReportData(null);
                setAllGroupsData(null);
                setError("");
              }
            }}
            size="small"
          >
            <ToggleButton value="as_on" sx={{ px: 2 }}>
              <CalendarToday sx={{ fontSize: 16, mr: 1 }} />
              As On Date
            </ToggleButton>
            <ToggleButton value="between" sx={{ px: 2 }}>
              <DateRange sx={{ fontSize: 16, mr: 1 }} />
              Between Dates
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>

        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={3}>
            <Autocomplete
              options={allOptions}
              value={selectedGroup}
              onChange={(_, newValue) => {
                setSelectedGroup(newValue);
                setReportData(null);
                setError("");
              }}
              loading={loadingGroups}
              getOptionLabel={(opt) => opt.account_name || ""}
              isOptionEqualToValue={(opt, val) =>
                opt.account_id === val.account_id
              }
              groupBy={(opt) => (opt.isStatic ? "— Category —" : "— Groups —")}
              renderOption={(props, opt) => (
                <li
                  {...props}
                  key={`${opt.isStatic ? "static" : "dynamic"}-${
                    opt.account_id
                  }`}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      width: "100%",
                    }}
                  >
                    <Typography variant="body2" sx={{ flexGrow: 1 }}>
                      {opt.account_name}
                    </Typography>
                    {opt.account_category && (
                      <Chip
                        label={opt.account_category}
                        size="small"
                        variant="outlined"
                        sx={{ fontSize: "0.65rem", height: 18 }}
                      />
                    )}
                  </Box>
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select Group"
                  error={!!groupsError}
                  helperText={groupsError || ""}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loadingGroups && <CircularProgress size={16} />}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          </Grid>

          {filterMode === "as_on" ? (
            <Grid item xs={12} md={2.5}>
              <TextField
                fullWidth
                type="date"
                label="As On Date"
                value={asOnDate}
                onChange={(e) => setAsOnDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          ) : (
            <>
              <Grid item xs={12} md={2.25}>
                <TextField
                  fullWidth
                  type="date"
                  label="From Date"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} md={2.25}>
                <TextField
                  fullWidth
                  type="date"
                  label="To Date"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            </>
          )}

          <Grid item xs={12} md={filterMode === "as_on" ? 6.5 : 4.5}>
            <Button
              variant="contained"
              size="large"
              startIcon={
                loadingReport ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  <AccountBalance />
                )
              }
              onClick={handleGenerateReport}
              disabled={loadingReport}
              sx={{ mr: 2 }}
            >
              {loadingReport ? "Generating..." : "Generate Report"}
            </Button>
            <Button
              variant="outlined"
              size="large"
              startIcon={<Refresh />}
              onClick={handleReset}
              disabled={loadingReport}
            >
              Reset
            </Button>
          </Grid>
        </Grid>
        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </Paper>

      {reportData && (
        <Paper elevation={2} sx={{ p: 3 }}>
          <Box sx={{ mb: 3 }}>
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
              Trial Balance
              {filterMode === "between" && (
                <Chip
                  label="Period Report"
                  size="small"
                  color="primary"
                  sx={{ ml: 2 }}
                />
              )}
            </Typography>
            <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap" }}>
              <Typography variant="body2" color="text.secondary">
                Group: <strong>{reportData.group_name}</strong>
                {reportData.group_category
                  ? ` (${reportData.group_category})`
                  : ""}
              </Typography>
              {filterMode === "as_on" ? (
                <Typography variant="body2" color="text.secondary">
                  As On:{" "}
                  <strong>{format(new Date(asOnDate), "dd/MM/yyyy")}</strong>
                </Typography>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Period:{" "}
                  <strong>
                    {format(new Date(fromDate), "dd/MM/yyyy")} to{" "}
                    {format(new Date(toDate), "dd/MM/yyyy")}
                  </strong>
                </Typography>
              )}
              {reportData.is_balanced ? (
                <Chip
                  label="Balanced ✓"
                  size="small"
                  color="success"
                  variant="outlined"
                />
              ) : (
                <Chip
                  label={`Difference: ${formatCurrency(reportData.difference)}`}
                  size="small"
                  color="warning"
                  variant="outlined"
                />
              )}
            </Box>
            <Typography
              variant="caption"
              color="primary"
              sx={{ mt: 1, display: "block" }}
            >
              💡 Double-click on any row to view detailed ledger
            </Typography>
          </Box>
          <Divider sx={{ mb: 3 }} />

          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
              gap: 2,
              flexWrap: "wrap",
            }}
          >
            <TextField
              size="small"
              placeholder="Search by account name or ID..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              sx={{ minWidth: 300 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
            <Button
              variant="outlined"
              startIcon={<Download />}
              onClick={generatePDF}
            >
              Download PDF
            </Button>
          </Box>

          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: "grey.100" }}>
                  <TableCell sx={{ fontWeight: 600 }}>#</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Account ID</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Account Name</TableCell>
                  {filterMode === "between" && (
                    <TableCell align="right" sx={{ fontWeight: 600 }}>
                      Opening
                    </TableCell>
                  )}
                  <TableCell
                    align="right"
                    sx={{ fontWeight: 600, color: "error.main" }}
                  >
                    {filterMode === "between" ? "Period " : ""}Debit
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{ fontWeight: 600, color: "text.primary" }}
                  >
                    {filterMode === "between" ? "Period " : ""}Credit
                  </TableCell>
                  {filterMode === "between" && (
                    <TableCell align="right" sx={{ fontWeight: 600 }}>
                      Closing
                    </TableCell>
                  )}
                  {filterMode === "as_on" && (
                    <TableCell align="right" sx={{ fontWeight: 600 }}>
                      Balance
                    </TableCell>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredAccounts.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={filterMode === "between" ? 7 : 5}
                      align="center"
                      sx={{ py: 4 }}
                    >
                      <Typography color="text.secondary">
                        No accounts found
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  <>
                    {filteredAccounts.map((acc, index) => {
                      const isDebit = acc.balance_type === "DEBIT";
                      const isOpeningDebit =
                        acc.period_opening_balance_type === "DEBIT";

                      return (
                        <TableRow
                          key={acc.account_id}
                          hover
                          onDoubleClick={() => handleAccountRowDoubleClick(acc)}
                          sx={{ cursor: "pointer" }}
                        >
                          <TableCell
                            sx={{
                              color: "text.secondary",
                              fontSize: "0.75rem",
                            }}
                          >
                            {index + 1}
                          </TableCell>
                          <TableCell>{acc.account_id}</TableCell>
                          <TableCell>
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 500 }}
                            >
                              {acc.account_name}
                            </Typography>
                          </TableCell>

                          {filterMode === "between" && (
                            <TableCell align="right">
                              {acc.period_opening_balance > 0 ? (
                                <Typography
                                  variant="body2"
                                  sx={{
                                    fontWeight: 600,
                                    color: isOpeningDebit
                                      ? "error.main"
                                      : "text.primary",
                                  }}
                                >
                                  {formatCurrency(acc.period_opening_balance)}{" "}
                                  {isOpeningDebit ? "Db" : "Cr"}
                                </Typography>
                              ) : (
                                <Typography
                                  variant="body2"
                                  color="text.disabled"
                                >
                                  -
                                </Typography>
                              )}
                            </TableCell>
                          )}

                          <TableCell align="right">
                            {filterMode === "between" ? (
                              acc.period_debit > 0 ? (
                                <Typography
                                  variant="body2"
                                  sx={{ color: "error.main" }}
                                >
                                  {formatCurrency(acc.period_debit)}
                                </Typography>
                              ) : (
                                <Typography
                                  variant="body2"
                                  color="text.disabled"
                                >
                                  -
                                </Typography>
                              )
                            ) : acc.total_debit > 0 ? (
                              <Typography
                                variant="body2"
                                sx={{ color: "error.main" }}
                              >
                                {formatCurrency(acc.total_debit)}
                              </Typography>
                            ) : (
                              <Typography variant="body2" color="text.disabled">
                                -
                              </Typography>
                            )}
                          </TableCell>

                          <TableCell align="right">
                            {filterMode === "between" ? (
                              acc.period_credit > 0 ? (
                                <Typography
                                  variant="body2"
                                  sx={{ color: "text.primary" }}
                                >
                                  {formatCurrency(acc.period_credit)}
                                </Typography>
                              ) : (
                                <Typography
                                  variant="body2"
                                  color="text.disabled"
                                >
                                  -
                                </Typography>
                              )
                            ) : acc.total_credit > 0 ? (
                              <Typography
                                variant="body2"
                                sx={{ color: "text.primary" }}
                              >
                                {formatCurrency(acc.total_credit)}
                              </Typography>
                            ) : (
                              <Typography variant="body2" color="text.disabled">
                                -
                              </Typography>
                            )}
                          </TableCell>

                          {filterMode === "between" && (
                            <TableCell align="right">
                              {acc.closing_balance > 0 ? (
                                <Typography
                                  variant="body2"
                                  sx={{
                                    fontWeight: 600,
                                    color: isDebit
                                      ? "error.main"
                                      : "text.primary",
                                  }}
                                >
                                  {formatCurrency(acc.closing_balance)}{" "}
                                  {isDebit ? "Db" : "Cr"}
                                </Typography>
                              ) : (
                                <Typography
                                  variant="body2"
                                  color="text.disabled"
                                >
                                  -
                                </Typography>
                              )}
                            </TableCell>
                          )}

                          {filterMode === "as_on" && (
                            <TableCell align="right">
                              {acc.closing_balance > 0 ? (
                                <Typography
                                  variant="body2"
                                  sx={{
                                    fontWeight: 600,
                                    color: isDebit
                                      ? "error.main"
                                      : "text.primary",
                                  }}
                                >
                                  {formatCurrency(acc.closing_balance)}{" "}
                                  {isDebit ? "Db" : "Cr"}
                                </Typography>
                              ) : (
                                <Typography
                                  variant="body2"
                                  color="text.disabled"
                                >
                                  -
                                </Typography>
                              )}
                            </TableCell>
                          )}
                        </TableRow>
                      );
                    })}

                    <TableRow sx={{ backgroundColor: "grey.100" }}>
                      <TableCell colSpan={3}>
                        <strong>
                          Grand Total ({filteredAccounts.length} accounts
                          {searchText ? " — filtered" : ""})
                        </strong>
                      </TableCell>
                      {filterMode === "between" && (
                        <TableCell align="right">
                          <Typography sx={{ fontWeight: 700 }}>
                            {reportData.opening_debit >
                            reportData.opening_credit
                              ? `${formatCurrency(
                                  reportData.opening_debit -
                                    reportData.opening_credit,
                                )} Db`
                              : reportData.opening_credit >
                                  reportData.opening_debit
                                ? `${formatCurrency(
                                    reportData.opening_credit -
                                      reportData.opening_debit,
                                  )} Cr`
                                : "-"}
                          </Typography>
                        </TableCell>
                      )}
                      <TableCell align="right">
                        <Typography
                          sx={{ fontWeight: 700, color: "error.main" }}
                        >
                          -
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography
                          sx={{ fontWeight: 700, color: "text.primary" }}
                        >
                          -
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography sx={{ fontWeight: 700 }}>
                          {reportData.total_debit > reportData.total_credit
                            ? `${formatCurrency(
                                reportData.total_debit -
                                  reportData.total_credit,
                              )} Db`
                            : reportData.total_credit > reportData.total_debit
                              ? `${formatCurrency(
                                  reportData.total_credit -
                                    reportData.total_debit,
                                )} Cr`
                              : "-"}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  </>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {allGroupsData && (
        <Paper elevation={2} sx={{ p: 3, mt: reportData ? 3 : 0 }}>
          <Box sx={{ mb: 3 }}>
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
              Trial Balance — All Groups
              {filterMode === "between" && (
                <Chip
                  label="Period Report"
                  size="small"
                  color="primary"
                  sx={{ ml: 2 }}
                />
              )}
            </Typography>
            <Box
              sx={{
                display: "flex",
                gap: 3,
                flexWrap: "wrap",
                alignItems: "center",
              }}
            >
              {filterMode === "as_on" ? (
                <Typography variant="body2" color="text.secondary">
                  As On:{" "}
                  <strong>{format(new Date(asOnDate), "dd/MM/yyyy")}</strong>
                </Typography>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Period:{" "}
                  <strong>
                    {format(new Date(fromDate), "dd/MM/yyyy")} to{" "}
                    {format(new Date(toDate), "dd/MM/yyyy")}
                  </strong>
                </Typography>
              )}
              {allGroupsData.is_balanced ? (
                <Chip
                  label="Balanced ✓"
                  size="small"
                  color="success"
                  variant="outlined"
                />
              ) : (
                <Chip
                  label={`Difference: ${formatCurrency(
                    allGroupsData.difference,
                  )}`}
                  size="small"
                  color="warning"
                  variant="outlined"
                />
              )}
            </Box>
          </Box>
          <Divider sx={{ mb: 3 }} />
          <TrialBalanceAllGroups
            reportData={allGroupsData}
            asOnDate={filterMode === "as_on" ? asOnDate : null}
            fromDate={filterMode === "between" ? fromDate : null}
            toDate={filterMode === "between" ? toDate : null}
            filterMode={filterMode}
          />
        </Paper>
      )}

      {!reportData && !allGroupsData && !error && !loadingReport && (
        <Paper elevation={1} sx={{ p: 6, textAlign: "center" }}>
          <Typography variant="h6" color="text.secondary">
            Select a group and date{filterMode === "between" ? "s" : ""}, then
            click "Generate Report" to view trial balance
          </Typography>
        </Paper>
      )}

      {/* ── LEDGER DIALOG ── */}
      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="lg"
        fullWidth
        PaperProps={{ sx: { height: "80vh" } }}
      >
        <DialogTitle>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Account Ledger — {selectedAccount?.account_name}
            </Typography>
            <IconButton onClick={handleCloseDialog} size="small">
              <Close />
            </IconButton>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Account ID: {selectedAccount?.account_id} | Category:{" "}
            {selectedAccount?.account_category || "N/A"}
          </Typography>
          <Typography variant="caption" color="primary">
            All transactions — use month filter to narrow down
          </Typography>
        </DialogTitle>

        <DialogContent dividers>
          {loadingLedger && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                minHeight: 200,
              }}
            >
              <CircularProgress />
            </Box>
          )}

          {ledgerError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {ledgerError}
            </Alert>
          )}

          {ledgerData && !loadingLedger && (
            <>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} md={4}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography color="text.secondary" gutterBottom>
                        Opening Balance
                      </Typography>
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        {formatCurrency(ledgerData.opening_balance)}
                      </Typography>
                      <Box
                        sx={{
                          mt: 1,
                          display: "inline-block",
                          px: 1,
                          py: 0.2,
                          borderRadius: 1,
                          fontSize: "0.875rem",
                          fontWeight: 600,
                          color: "white",
                          backgroundColor: "primary.main",
                        }}
                      >
                        {ledgerData.opening_balance_type}
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography color="text.secondary" gutterBottom>
                        Total Debit
                      </Typography>
                      <Typography
                        variant="h6"
                        sx={{ fontWeight: 600, color: "error.main" }}
                      >
                        {formatCurrency(ledgerData.total_debit)}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ mt: 1, display: "block" }}
                      >
                        {ledgerData.transaction_count} transactions
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography color="text.secondary" gutterBottom>
                        Total Credit
                      </Typography>
                      <Typography
                        variant="h6"
                        sx={{ fontWeight: 600, color: "text.primary" }}
                      >
                        {formatCurrency(ledgerData.total_credit)}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ mt: 1, display: "block" }}
                      >
                        {ledgerData.transaction_count} transactions
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              <Box
                sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}
              >
                <TextField
                  type="month"
                  size="small"
                  label="Filter by Month"
                  value={dialogMonthFilter}
                  onChange={(e) => setDialogMonthFilter(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  sx={{ width: 200 }}
                />
                {dialogMonthFilter && (
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => setDialogMonthFilter("")}
                  >
                    Clear Filter
                  </Button>
                )}
                <Typography variant="caption" color="text.secondary">
                  Showing {filteredTransactions.length} of{" "}
                  {ledgerData.transactions.length} transactions
                </Typography>
              </Box>

              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ backgroundColor: "grey.100" }}>
                      <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>
                        Voucher No.
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Narration</TableCell>
                      <TableCell
                        align="right"
                        sx={{ fontWeight: 600, color: "error.main" }}
                      >
                        Debit
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{ fontWeight: 600, color: "text.primary" }}
                      >
                        Credit
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>
                        Balance
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow sx={{ backgroundColor: "grey.50" }}>
                      <TableCell colSpan={3}>
                        <strong>Opening Balance</strong>
                      </TableCell>
                      <TableCell align="right">-</TableCell>
                      <TableCell align="right">-</TableCell>
                      <TableCell align="right">
                        <Typography>
                          {formatCurrency(ledgerData.opening_balance)}
                        </Typography>
                        <Box
                          component="span"
                          sx={{
                            display: "inline-block",
                            ml: 0.5,
                            px: 0.5,
                            py: 0.5,
                            borderRadius: 20,
                            fontSize: "0.6rem",
                            fontWeight: 600,
                            lineHeight: 1,
                            color: "white",
                            backgroundColor: "primary.main",
                          }}
                        >
                          {ledgerData.opening_balance_type}
                        </Box>
                      </TableCell>
                    </TableRow>

                    {filteredTransactions.length > 0 ? (
                      filteredTransactions.map((txn, index) => {
                        const isHighlighted =
                          highlightVoucher === txn.voucher_number;
                        return (
                          <Tooltip
                            key={index}
                            title="Double-click to open voucher"
                            placement="left"
                          >
                            <TableRow
                              ref={isHighlighted ? highlightTxnRowRef : null}
                              hover
                              onDoubleClick={() => handleTxnRowDoubleClick(txn)}
                              sx={{
                                cursor: "pointer",
                                backgroundColor: isHighlighted
                                  ? "rgba(4, 126, 207, 0.15)"
                                  : "transparent",
                                "&:hover": {
                                  backgroundColor: isHighlighted
                                    ? "rgba(4, 126, 207, 0.25)"
                                    : "action.hover",
                                },
                              }}
                            >
                              <TableCell>
                                {format(new Date(txn.date), "dd/MM/yyyy")}
                              </TableCell>
                              <TableCell>{txn.voucher_number}</TableCell>
                              <TableCell>{txn.narration}</TableCell>
                              <TableCell
                                align="right"
                                sx={{ color: "error.main" }}
                              >
                                {txn.debit_amount > 0
                                  ? formatCurrency(txn.debit_amount)
                                  : "-"}
                              </TableCell>
                              <TableCell
                                align="right"
                                sx={{ color: "text.primary" }}
                              >
                                {txn.credit_amount > 0
                                  ? formatCurrency(txn.credit_amount)
                                  : "-"}
                              </TableCell>
                              <TableCell align="right">
                                <strong>
                                  {formatCurrency(txn.running_balance)}
                                </strong>{" "}
                                <Box
                                  component="span"
                                  sx={{
                                    display: "inline-block",
                                    ml: 0.5,
                                    px: 0.5,
                                    py: 0.5,
                                    borderRadius: 20,
                                    fontSize: "0.6rem",
                                    fontWeight: 600,
                                    lineHeight: 1,
                                    color: "white",
                                    backgroundColor: "primary.main",
                                  }}
                                >
                                  {txn.balance_type}
                                </Box>
                              </TableCell>
                            </TableRow>
                          </Tooltip>
                        );
                      })
                    ) : (
                      <TableRow>
                        <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                          <Typography color="text.secondary">
                            {dialogMonthFilter
                              ? "No transactions for selected month"
                              : "No transactions found"}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}

                    <TableRow sx={{ backgroundColor: "grey.50" }}>
                      <TableCell colSpan={3}>
                        <strong>Closing Balance</strong>
                      </TableCell>
                      <TableCell align="right">
                        <strong>
                          {formatCurrency(ledgerData.total_debit)}
                        </strong>
                      </TableCell>
                      <TableCell align="right">
                        <strong>
                          {formatCurrency(ledgerData.total_credit)}
                        </strong>
                      </TableCell>
                      <TableCell align="right">
                        <strong>
                          {formatCurrency(ledgerData.closing_balance)}
                        </strong>{" "}
                        <Box
                          component="span"
                          sx={{
                            display: "inline-block",
                            ml: 0.5,
                            px: 0.5,
                            py: 0.5,
                            borderRadius: 20,
                            fontSize: "0.6rem",
                            fontWeight: 600,
                            lineHeight: 1,
                            color: "white",
                            backgroundColor: "primary.main",
                          }}
                        >
                          {ledgerData.closing_balance_type}
                        </Box>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={handleCloseDialog} variant="outlined">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TrialBalanceByGroup;
